import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Radio, message } from 'antd';
import { SendOutlined, EditOutlined, CheckOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { renderMarkdownSafely } from '../../pages/DashboardUtils';
import apiClient from '../../services/api';
import { getSelectedFolder, getSelectedKB, getSelectedTopic } from '../../hooks/useDashboardGlobalVar';

const { TextArea } = Input;

const STEPS = { INTENT: 'intent', BRIEF: 'brief', TOC: 'toc', DONE: 'done' };
const STEP_NUMBERS = { [STEPS.INTENT]: 1, [STEPS.BRIEF]: 2, [STEPS.TOC]: 3, [STEPS.DONE]: 3 };

const fontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const DOC_TYPES = [
  { key: 'product_brief', label: 'Product Brief', desc: 'Problem, solution, architecture & use cases' },
  { key: 'tutorial', label: 'Tutorial', desc: 'Step-by-step guide with prerequisites' },
  { key: 'reference', label: 'Reference', desc: 'Scannable API or concept reference' },
  { key: 'technical_report', label: 'Technical Report', desc: 'Findings, methodology & recommendations' },
  { key: 'explainer', label: 'Explainer', desc: 'Concept overview — why it matters' },
  { key: 'runbook', label: 'Runbook', desc: 'Operational procedure with rollback' },
  { key: 'comparison', label: 'Comparison', desc: 'Options evaluated against criteria' },
];

/**
 * AIWriterPanel - Conversational AI Writer assistant with 3-step flow
 * Step 1: Intent Capture
 * Step 2: Brief Review & Confirm
 * Step 3: TOC Review & Edit
 */
const AIWriterPanel = ({ selectedNote, currentFilterState = {}, onBack }) => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);
  const chatHistoryRef = useRef(null);
  const inputRef = useRef(null);

  const [step, setStep] = useState(STEPS.INTENT);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  // Step 1 state
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [capturedIntent, setCapturedIntent] = useState('');
  const [intentConfirmed, setIntentConfirmed] = useState(false);
  const [screenedSources, setScreenedSources] = useState([]);

  // Step 2 state
  const [writingBrief, setWritingBrief] = useState(null);
  const [briefJson, setBriefJson] = useState(null);
  const [briefConfirmed, setBriefConfirmed] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);

  // Step 3 state
  const [tocContent, setTocContent] = useState('');
  const [tocJson, setTocJson] = useState(null);
  const [tocGenerated, setTocGenerated] = useState(false);
  const [tocMessageId, setTocMessageId] = useState(null);
  const [tocConversationHistory, setTocConversationHistory] = useState([]);

  // Get folder name for context display (same as NoteListAsk)
  const currentFolder = getSelectedFolder();
  const currentTopic = getSelectedTopic();
  const currentKB = getSelectedKB();
  const contextName = currentTopic?.name || currentKB?.name || currentFolder || t('dashboard:all_notes', { defaultValue: 'All Notes' });

  // Build search filter params (same logic as NoteListAsk)
  const getSearchFilters = useCallback(() => {
    const folder = getSelectedFolder();
    const topic = getSelectedTopic();
    const kb = getSelectedKB();
    return {
      filterType: folder === 'private' ? 'private' :
        folder === 'collaborative' ? 'collaborative' :
          folder === 'pinned' ? 'pinned' :
            folder === 'recent' ? 'recent' : 'all',
      topicId: topic ? topic.topic_id : '',
      kbId: kb ? kb.kb_id : '',
    };
  }, []);

  // Welcome message on mount
  useEffect(() => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: t('dashboard:aiwriter_welcome', {
        defaultValue: "Welcome to AI Doc Writer! First, select the type of document you'd like to create."
      })
    }]);
  }, [t]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (chatHistoryRef.current) {
      setTimeout(() => {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
      }, 50);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, step, writingBrief, tocContent, scrollToBottom]);

  const addMessage = useCallback((role, content, extra = {}) => {
    const msg = { id: Date.now().toString() + Math.random(), role, content, ...extra };
    setMessages(prev => [...prev, msg]);
    return msg;
  }, []);

  // ──────────── Step 1: Intent Capture ────────────
  const handleIntentSubmit = async (intentText) => {
    const text = (intentText || inputValue).trim();
    if (!text || isLoading) return;

    addMessage('user', text);
    setInputValue('');
    setCapturedIntent(text);
    setIsLoading(true);

    let serverMessage = '';
    let sources = [];
    try {
      const filters = getSearchFilters();
      const { data } = await apiClient.post('/notes/aiwriter/intent-capture', {
        noteId: selectedNote?.id,
        userIntent: text,
        ...filters
      });
      serverMessage = data?.message || '';
      sources = data?.relevantNotes || [];
      setScreenedSources(sources);
    } catch (err) {
      console.warn('[AIWriterPanel] intent-capture call failed:', err.message);
    } finally {
      setIsLoading(false);
    }

    // Show source info and prompt user to confirm or refine
    if (sources.length < 2) {
      const notEnough = t('dashboard:aiwriter_not_enough_sources', {
        defaultValue: 'We need more sources to proceed. Could you describe your document in more detail? For example, specify the topic, key points, or angle you\'d like to cover.'
      });
      const fullMsg = serverMessage ? `${serverMessage}\n\n${notEnough}` : notEnough;
      addMessage('assistant', fullMsg);
    } else {
      const hint = t('dashboard:aiwriter_intent_hint', {
        defaultValue: 'You can also refine your request by providing more details about the document you\'d like to create.'
      });
      const fullMsg = serverMessage ? `${serverMessage}\n\n${hint}` : hint;
      addMessage('assistant', fullMsg);
    }
  };

  const handleConfirmIntent = () => {
    setIntentConfirmed(true);
    setMessages([]);
    setConversationHistory([]);
    addMessage('assistant', t('dashboard:aiwriter_intent_confirmed', {
      defaultValue: "Great! Let me create a writing brief for you..."
    }));
    setStep(STEPS.BRIEF);
    handleCreateBrief(capturedIntent, [], screenedSources);
  };

  // ──────────── Step 2: Brief Creation ────────────
  const handleCreateBrief = async (intent, history, sources = screenedSources) => {
    setIsLoading(true);
    try {
      const filters = getSearchFilters();
      const { data } = await apiClient.post('/notes/aiwriter/create-brief', {
        noteId: selectedNote?.id,
        userIntent: intent,
        conversationHistory: history,
        screenedSources: sources,
        ...filters
      });

      if (data?.type === 'questions') {
        addMessage('assistant', data.message);
        setConversationHistory(prev => [...prev, { role: 'assistant', content: data.message }]);
      } else if (data?.type === 'brief' && (data?.brief_md || data?.brief)) {
        setWritingBrief(data.brief_md || data.brief);
        setBriefJson(data.brief);
        addMessage('assistant', t('dashboard:aiwriter_brief_ready', {
          defaultValue: "Here's the writing brief I've prepared. Review and confirm, or let me know what to change."
        }));
      } else {
        addMessage('assistant', t('dashboard:aiwriter_brief_error', {
          defaultValue: "Sorry, I couldn't generate a brief. Please try again."
        }));
      }
    } catch (err) {
      console.warn('[AIWriterPanel] create-brief call:', err.message);
      addMessage('assistant', t('dashboard:aiwriter_brief_error', {
        defaultValue: "Sorry, I couldn't generate a brief. Please try again."
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBriefReply = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    addMessage('user', text);
    setInputValue('');

    const newHistory = [...conversationHistory, { role: 'user', content: text }];
    setConversationHistory(newHistory);

    // If brief is already shown and user is requesting changes
    if (writingBrief) {
      setWritingBrief(null);
    }

    await handleCreateBrief(capturedIntent, newHistory);
  };

  const handleConfirmBrief = () => {
    setBriefConfirmed(true);
    setMessages([]);
    setConversationHistory([]);
    addMessage('assistant', t('dashboard:aiwriter_brief_confirmed', {
      defaultValue: "Brief confirmed! Now generating the table of contents..."
    }));
    setStep(STEPS.TOC);
    handleCreateToc();
  };

  // ──────────── Step 3: TOC Creation ────────────
  const handleCreateToc = async (history = tocConversationHistory) => {
    setIsLoading(true);
    try {
      const filters = getSearchFilters();
      const { data } = await apiClient.post('/notes/aiwriter/create-toc', {
        noteId: selectedNote?.id,
        writingBrief: writingBrief,
        screenedSources: screenedSources,
        documentType: selectedDocType,
        conversationHistory: history,
        ...filters
      });

      const tocMd = data?.toc_md || data?.toc || '';
      if (tocMd) {
        setTocContent(typeof tocMd === 'string' ? tocMd : JSON.stringify(tocMd));
        setTocJson(data?.toc || null);
        setTocGenerated(true);
        // Record assistant's TOC response in conversation history
        const tocResponseStr = typeof tocMd === 'string' ? tocMd : JSON.stringify(tocMd);
        setTocConversationHistory(prev => [...prev, { role: 'assistant', content: tocResponseStr }]);
        const tocMsg = addMessage('assistant', t('dashboard:aiwriter_toc_ready', {
          defaultValue: "Here's the table of contents."
        }));
        setTocMessageId(tocMsg.id);
      } else {
        addMessage('assistant', t('dashboard:aiwriter_toc_error', {
          defaultValue: "Sorry, I couldn't generate the table of contents. Please try again."
        }));
      }
    } catch (err) {
      console.warn('[AIWriterPanel] create-toc call:', err.message);
      addMessage('assistant', t('dashboard:aiwriter_toc_error', {
        defaultValue: "Sorry, I couldn't generate the table of contents. Please try again."
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmToc = async () => {
    setStep(STEPS.DONE);
    addMessage('assistant', t('dashboard:aiwriter_toc_confirmed', {
      defaultValue: "I've collected all the information needed and will start generating the document. You can click 'Doc Status' on the AI Doc Writer tool to check the progress."
    }));
    try {
      const filters = getSearchFilters();
      await apiClient.post('/notes/aiwriter/confirm-toc', {
        title: briefJson?.suggested_title || '',
        meta: { brief: briefJson, toc: tocJson, screenedSources, capturedIntent, documentType: selectedDocType },
        ...filters
      });
    } catch (err) {
      console.warn('[AIWriterPanel] confirm-toc save failed:', err.message);
    }
  };

  // ──────────── Input handler ────────────
  const handleTocReply = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    addMessage('user', text);
    setInputValue('');
    const newHistory = [...tocConversationHistory, { role: 'user', content: text }];
    setTocConversationHistory(newHistory);
    handleCreateToc(newHistory);
  };

  const handleSend = () => {
    if (step === STEPS.INTENT && !intentConfirmed) {
      handleIntentSubmit();
    } else if (step === STEPS.BRIEF && !briefConfirmed) {
      handleBriefReply();
    } else if (step === STEPS.TOC) {
      handleTocReply();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showInput = (step === STEPS.INTENT && selectedDocType && !intentConfirmed) ||
                    (step === STEPS.BRIEF && !briefConfirmed) ||
                    (step === STEPS.TOC);

  // ──────────── Render helpers ────────────
  const renderStepIndicator = () => {
    const currentNum = STEP_NUMBERS[step] || 1;
    const steps = [
      { num: 1, label: t('dashboard:aiwriter_step_intent', { defaultValue: 'Intent' }) },
      { num: 2, label: t('dashboard:aiwriter_step_brief', { defaultValue: 'Brief' }) },
      { num: 3, label: t('dashboard:aiwriter_step_toc', { defaultValue: 'TOC' }) }
    ];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              backgroundColor: s.num <= currentNum ? '#a855f7' : '#e5e7eb',
              color: s.num <= currentNum ? '#fff' : '#9ca3af',
              fontSize: 11, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily
            }}>
              {s.num < currentNum ? <CheckOutlined style={{ fontSize: 10 }} /> : s.num}
            </div>
            <span style={{
              fontSize: 11, color: s.num === currentNum ? '#a855f7' : '#9ca3af',
              fontWeight: s.num === currentNum ? 600 : 400, fontFamily
            }}>
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <div style={{ width: 12, height: 1, backgroundColor: '#d1d5db' }} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const handleDocTypeSelect = (docType) => {
    setSelectedDocType(docType);
    addMessage('user', `Document type: ${DOC_TYPES.find(d => d.key === docType)?.label}`);
    addMessage('assistant', t('dashboard:aiwriter_describe_intent', {
      defaultValue: "Great choice! Now describe the main topic and core ideas of the document you want to create. Be specific."
    }));
  };

  const renderDocTypeSelector = () => {
    if (selectedDocType) return null;
    return (
      <div style={{ margin: '8px 0 8px 44px' }}>
        <Radio.Group
          onChange={e => handleDocTypeSelect(e.target.value)}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 45%), 1fr))', gap: '6px 16px' }}
        >
          {DOC_TYPES.map(dt => (
            <Radio key={dt.key} value={dt.key} style={{ fontSize: 13, fontFamily }}>
              <div>
                <div style={{ fontWeight: 500, color: '#374151' }}>{dt.label}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{dt.desc}</div>
              </div>
            </Radio>
          ))}
        </Radio.Group>
      </div>
    );
  };

  const renderIntentConfirmation = () => {
    if (!capturedIntent || intentConfirmed || screenedSources.length < 2) return null;
    return (
      <div style={{ margin: '8px 0 8px 44px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" type="primary" onClick={handleConfirmIntent}
          style={{ backgroundColor: '#a855f7', borderColor: '#a855f7' }}>
          {t('dashboard:aiwriter_generate_brief', { defaultValue: '→ Generate Brief' })}
        </Button>
      </div>
    );
  };

  const renderBriefCard = () => {
    if (!writingBrief || briefConfirmed) return null;
    return (
      <div style={{
        margin: '8px 0 8px 44px',
        padding: 16,
        borderRadius: 12,
        border: '1px solid #e9d5ff',
        backgroundColor: '#faf5ff'
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed', marginBottom: 12, fontFamily }}>
          {t('dashboard:aiwriter_writing_brief', { defaultValue: 'Writing Brief' })}
        </div>
        <div
          className="aiwriter-message-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdownSafely(typeof writingBrief === 'string' ? writingBrief : '') }}
          style={{ fontSize: 14, color: '#374151', lineHeight: '22px', fontFamily }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button size="small" type="primary" onClick={handleConfirmBrief}
            style={{ backgroundColor: '#a855f7', borderColor: '#a855f7' }}>
            {t('dashboard:aiwriter_create_toc', { defaultValue: '→ Create TOC' })}
          </Button>
        </div>
      </div>
    );
  };

  const renderTocCard = () => {
    if (!tocGenerated) return null;
    return (
      <div style={{
        margin: '8px 0 8px 44px',
        padding: 16,
        borderRadius: 12,
        border: '1px solid #e9d5ff',
        backgroundColor: '#faf5ff'
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed', marginBottom: 12, fontFamily }}>
          {t('dashboard:aiwriter_toc_title', { defaultValue: 'Table of Contents' })}
        </div>
        <div
          className="aiwriter-message-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdownSafely(typeof tocContent === 'string' ? tocContent : '') }}
          style={{ fontSize: 14, color: '#374151', lineHeight: '22px', fontFamily }}
        />
        {step !== STEPS.DONE && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Button size="small" type="primary" onClick={handleConfirmToc}
              style={{ backgroundColor: '#a855f7', borderColor: '#a855f7' }}>
              {t('dashboard:aiwriter_generate_document', { defaultValue: '→ Generate Document' })}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        .aiwriter-message-content p {
          margin: 0;
          font-size: 14px;
          line-height: 20px;
          font-weight: 400;
        }
        .aiwriter-message-content p + p {
          margin-top: 8px;
        }
        .aiwriter-message-content ul, .aiwriter-message-content ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        .aiwriter-message-content li {
          font-size: 14px;
          line-height: 20px;
          font-weight: 400;
        }
        @keyframes aiwriterBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onBack && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              size="small"
              style={{ color: '#6b7280', marginRight: 4 }}
            />
          )}
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <EditOutlined style={{ fontSize: 16, color: '#fff' }} />
          </div>
          <div>
            <h3 style={{
              margin: 0, fontSize: 14, fontWeight: 600, color: '#111827',
              lineHeight: '20px', fontFamily, WebkitFontSmoothing: 'antialiased'
            }}>
              {t('dashboard:form.ai_writer_assistant', { defaultValue: 'AI Doc Writer' })}
            </h3>
            <div style={{
              fontSize: 12, color: '#6b7280', lineHeight: '16px', fontFamily,
              WebkitFontSmoothing: 'antialiased'
            }}>
              {t('dashboard:aiwriter_library', { defaultValue: 'Library:' })} {contextName}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {renderStepIndicator()}
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={chatHistoryRef}
        style={{
          flex: 1, overflowY: 'auto', padding: 24,
          display: 'flex', flexDirection: 'column', gap: 16
        }}
      >
        {messages.map((msg) => (
          <div key={msg.id}>
            <div style={{
              display: 'flex', gap: 12,
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}>
              {msg.role === 'assistant' && (
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  backgroundColor: '#f3e8ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <EditOutlined style={{ fontSize: 16, color: '#a855f7' }} />
                </div>
              )}
              <div style={{
                flex: 1, borderRadius: 16, padding: '12px 16px',
                backgroundColor: msg.role === 'user' ? '#eff6ff' : '#f3f4f6',
                color: '#111827'
              }}>
                <div
                  className="aiwriter-message-content"
                  dangerouslySetInnerHTML={{
                    __html: msg.id === 'welcome'
                      ? msg.content.replace(/\n/g, '<br/>').replace(/•/g, '&bull;')
                      : renderMarkdownSafely(msg.content)
                  }}
                  style={{
                    fontSize: 14, lineHeight: '20px', fontWeight: 400,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    fontFamily, WebkitFontSmoothing: 'antialiased'
                  }}
                />
              </div>
              {msg.role === 'user' && (
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  backgroundColor: '#d1d5db',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, fontSize: 12, fontWeight: 500, color: '#374151'
                }}>
                  {t('common:you_short', { defaultValue: 'You' })}
                </div>
              )}
            </div>

            {/* Render doc type selector after welcome message */}
            {msg.id === 'welcome' && step === STEPS.INTENT && !selectedDocType && renderDocTypeSelector()}

            {/* Render intent confirmation after the AI message asking to confirm */}
            {msg.id !== 'welcome' && msg.role === 'assistant' && step === STEPS.INTENT &&
              capturedIntent && !intentConfirmed &&
              messages[messages.length - 1]?.id === msg.id && renderIntentConfirmation()}

            {/* Render brief card after the AI message saying brief is ready */}
            {msg.role === 'assistant' && step === STEPS.BRIEF &&
              writingBrief && !briefConfirmed &&
              messages[messages.length - 1]?.id === msg.id && renderBriefCard()}

            {/* Render TOC card after the AI message saying TOC is ready */}
            {msg.role === 'assistant' && (step === STEPS.TOC || step === STEPS.DONE) &&
              tocGenerated &&
              msg.id === tocMessageId && renderTocCard()}
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: '#f3e8ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <EditOutlined style={{ fontSize: 16, color: '#a855f7' }} />
            </div>
            <div style={{
              borderRadius: 16, padding: '12px 16px',
              backgroundColor: '#f3f4f6',
              display: 'flex', gap: 4
            }}>
              {[0, 150, 300].map(delay => (
                <span key={delay} style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: '#9ca3af',
                  animation: 'aiwriterBounce 1s infinite',
                  animationDelay: `${delay}ms`
                }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      {showInput && (
        <div style={{
          borderTop: '1px solid #e5e7eb',
          padding: 16, display: 'flex', gap: 8
        }}>
          <TextArea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              step === STEPS.INTENT
                ? t('dashboard:aiwriter_input_intent', { defaultValue: 'Describe what you want to write...' })
                : step === STEPS.TOC
                  ? t('dashboard:aiwriter_input_toc', { defaultValue: 'Suggest changes to the table of contents...' })
                  : t('dashboard:aiwriter_input_brief', { defaultValue: 'Reply to refine the brief, or confirm above...' })
            }
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{
              flex: 1, borderRadius: 8, padding: '12px 16px',
              fontSize: 14, lineHeight: '20px', resize: 'none',
              border: '1px solid #d1d5db', fontFamily
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            style={{
              width: 44, height: 44, borderRadius: 8, border: 'none',
              backgroundColor: inputValue.trim() && !isLoading ? '#a855f7' : '#e5e7eb',
              color: inputValue.trim() && !isLoading ? '#fff' : '#9ca3af',
              cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <SendOutlined style={{ fontSize: 18 }} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AIWriterPanel;
